import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle, Loader2, PawPrint } from "lucide-react";

const serviceTypes = {
  veterinario: [
    { value: "visita", label: "Visita generale" },
    { value: "vaccinazione", label: "Vaccinazione" },
    { value: "controllo", label: "Controllo" },
    { value: "chirurgia", label: "Chirurgia" },
    { value: "emergenza", label: "Emergenza" }
  ],
  dog_sitter: [
    { value: "passeggiata", label: "Passeggiata" },
    { value: "pensione_giorno", label: "Pensione giornaliera" },
    { value: "pensione_notte", label: "Pensione notturna" },
    { value: "pet_sitting", label: "Pet sitting a domicilio" }
  ],
  cinofilo: [
    { value: "lezione", label: "Lezione individuale" },
    { value: "consulenza", label: "Consulenza comportamentale" },
    { value: "corso", label: "Corso di gruppo" },
    { value: "valutazione", label: "Valutazione iniziale" }
  ],
  assicurazione: [
    { value: "consulenza", label: "Consulenza gratuita" },
    { value: "preventivo", label: "Richiesta preventivo" }
  ],
  negozio: [
    { value: "consulenza", label: "Consulenza nutrizionale" },
    { value: "appuntamento", label: "Appuntamento in negozio" }
  ]
};

export default function BookingDialog({ 
  open, 
  onOpenChange, 
  professional, 
  isClinic = false 
}) {
  const [pets, setPets] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  
  const [booking, setBooking] = useState({
    professional_id: "",
    service_type: "",
    pet_id: "",
    date: "",
    time_slot: "",
    duration_minutes: 60,
    notes: ""
  });

  useEffect(() => {
    if (open && professional) {
      fetchPets();
      setBooking(prev => ({
        ...prev,
        professional_id: isClinic ? professional.clinic_id : professional.professional_id,
        service_type: "",
        date: "",
        time_slot: ""
      }));
      setBookingSuccess(false);
      setAvailableSlots([]);
    }
  }, [open, professional]);

  useEffect(() => {
    if (booking.date && booking.professional_id) {
      fetchAvailableSlots();
    }
  }, [booking.date]);

  const fetchPets = async () => {
    try {
      const response = await authFetch(`${API}/pets`);
      if (response.ok) {
        const petsData = await response.json();
        setPets(petsData);
        if (petsData.length > 0) {
          setBooking(prev => ({ ...prev, pet_id: petsData[0].pet_id }));
        }
      }
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const response = await authFetch(
        `${API}/bookings/available-slots/${booking.professional_id}?date=${booking.date}`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await authFetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(booking)
      });
      
      if (response.ok) {
        setBookingSuccess(true);
        toast.success("Prenotazione inviata con successo!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella prenotazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const professionType = isClinic ? "veterinario" : (professional?.profession_type || "veterinario");
  const services = serviceTypes[professionType] || serviceTypes.veterinario;
  
  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  if (!professional) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] rounded-2xl bg-white max-h-[90vh] overflow-y-auto">
        {bookingSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-emerald-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Prenotazione Inviata!
            </h3>
            <p className="text-slate-600 mb-6">
              La tua richiesta è stata inviata a <strong>{professional.name}</strong>. 
              Riceverai una conferma a breve.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 text-left mb-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Data</span>
                  <p className="font-medium text-slate-800">{booking.date}</p>
                </div>
                <div>
                  <span className="text-slate-500">Ora</span>
                  <p className="font-medium text-slate-800">{booking.time_slot}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Servizio</span>
                  <p className="font-medium text-slate-800">
                    {services.find(s => s.value === booking.service_type)?.label || booking.service_type}
                  </p>
                </div>
              </div>
            </div>
            <Button 
              onClick={() => onOpenChange(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-5"
            >
              Chiudi
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Prenota appuntamento
              </DialogTitle>
              <p className="text-sm text-slate-500">{professional.name}</p>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Service Type */}
              <div>
                <Label className="text-slate-700 text-sm mb-1.5 block">Tipo di servizio *</Label>
                <Select 
                  value={booking.service_type} 
                  onValueChange={(value) => setBooking({...booking, service_type: value})}
                >
                  <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200" data-testid="booking-service-select">
                    <SelectValue placeholder="Seleziona servizio" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Pet Selection */}
              {pets.length > 0 && (
                <div>
                  <Label className="text-slate-700 text-sm mb-1.5 block">Per quale animale?</Label>
                  <Select 
                    value={booking.pet_id} 
                    onValueChange={(value) => setBooking({...booking, pet_id: value})}
                  >
                    <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200" data-testid="booking-pet-select">
                      <SelectValue placeholder="Seleziona animale" />
                    </SelectTrigger>
                    <SelectContent>
                      {pets.map((pet) => (
                        <SelectItem key={pet.pet_id} value={pet.pet_id}>
                          <div className="flex items-center gap-2">
                            <PawPrint size={14} />
                            {pet.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Date */}
              <div>
                <Label className="text-slate-700 text-sm mb-1.5 block">Data *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    type="date"
                    min={today}
                    value={booking.date}
                    onChange={(e) => setBooking({...booking, date: e.target.value, time_slot: ""})}
                    className="pl-10 rounded-xl bg-slate-50 border-slate-200 focus:border-blue-500"
                    required
                    data-testid="booking-date-input"
                  />
                </div>
              </div>
              
              {/* Time Slots */}
              {booking.date && (
                <div>
                  <Label className="text-slate-700 text-sm mb-1.5 block">Orario *</Label>
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="animate-spin text-blue-600" size={24} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          type="button"
                          disabled={!slot.available}
                          onClick={() => setBooking({...booking, time_slot: slot.time})}
                          className={`py-2 px-1 rounded-lg text-sm font-medium transition-all ${
                            booking.time_slot === slot.time
                              ? 'bg-blue-600 text-white'
                              : slot.available
                                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                : 'bg-slate-50 text-slate-300 cursor-not-allowed line-through'
                          }`}
                          data-testid={`time-slot-${slot.time}`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Notes */}
              <div>
                <Label className="text-slate-700 text-sm mb-1.5 block">Note (opzionale)</Label>
                <Textarea
                  value={booking.notes}
                  onChange={(e) => setBooking({...booking, notes: e.target.value})}
                  placeholder="Aggiungi dettagli o richieste particolari..."
                  className="rounded-xl bg-slate-50 border-slate-200 focus:border-blue-500"
                  rows={3}
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || !booking.service_type || !booking.date || !booking.time_slot}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-5"
                data-testid="booking-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="animate-spin" size={18} />
                    Invio in corso...
                  </span>
                ) : (
                  "Conferma prenotazione"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
